import React, { useCallback, useContext } from 'react';
import { Typography } from '@mui/material';
import { generatePath } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';
import type { EntityListColumn } from 'c-pagination';
import { PaginatedEntityTableContext } from 'c-pagination/EntityPaginationContext';
import type { PopDisplays_JobStatus } from 'c-sdk';
import { useCommonTranslation, useModelFieldTranslation } from 'c-translation';
import ExpandableHtmlContent from './ExpandableHtmlContent';

/** `#14323` in a task name — the record the job is about. */
const recordIdSegment = /^#(\d+)$/;

/** Every job with a source points at a campaign; nothing else is `statusable` today. */
const campaignType = 'App\\Models\\Portal\\Campaign';

/**
 * The task name, with its `(parenthesised)`, `#id` and email parts clickable.
 *
 * `#id` opens that campaign in a new tab. It is a real anchor rather than a click handler so
 * middle-click and cmd-click behave normally and the target shows on hover. Narrowing the list to
 * one campaign is still available from the filter button in the last column, which is where that
 * behaviour belongs.
 *
 * Everything else has nowhere to link to, so it becomes the search term.
 */
const TaskNameCell: EntityListColumn<PopDisplays_JobStatus>['RenderCell'] = ({ entity }) => {
    const t = useCommonTranslation();
    const mt = useModelFieldTranslation();
    const { setSearchTerm } = useContext(PaginatedEntityTableContext);

    const { statusable_type: statusableType, statusable_id: statusableId } = entity;

    /**
     * Only when the clicked id is the one this job belongs to. A name could carry some other `#`,
     * and linking to a record the row isn't about would send the reader somewhere unrelated.
     */
    const recordIdFor = useCallback(
        (segment: string) => {
            const match = segment.match(recordIdSegment);
            if (!match || !statusableType) return null;

            return String(statusableId) === match[1] ? match[1] : null;
        },
        [statusableType, statusableId],
    );

    const segmentHref = useCallback(
        (segment: string) => {
            const recordId = recordIdFor(segment);

            if (recordId == null || statusableType !== campaignType) return undefined;

            return generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id: recordId });
        },
        [recordIdFor, statusableType],
    );

    const onSegmentClick = useCallback(
        (segment: string) => {
            // Anything with a link is handled by the anchor itself.
            if (segmentHref(segment) != null) return;

            setSearchTerm(segment);
        },
        [segmentHref, setSearchTerm],
    );

    const segmentTitle = useCallback(
        (segment: string) => {
            if (segmentHref(segment) != null) {
                return t('Modules.PopDisplays.JobStatus.table.openRecord', {
                    type: mt('PopDisplays_JobStatus', 'statusable_type', String(statusableType)),
                });
            }

            return t('Modules.PopDisplays.JobStatus.table.searchForSegment', { term: segment });
        },
        [segmentHref, t, mt, statusableType],
    );

    return (
        // No max width: task names are the longest content in the table and there is plenty of
        // room, so let the column claim it and wrap at the cell edge instead of at 400px.
        // component="div" because ExpandableHtmlContent renders a div, which is invalid inside
        // Typography's default <p>.
        <Typography
            component="div"
            fontSize="small"
            align="left"
            sx={{ minWidth: 320, whiteSpace: 'normal', overflowWrap: 'break-word' }}
        >
            <ExpandableHtmlContent
                contentHtml={entity.name}
                highlight
                onSegmentClick={onSegmentClick}
                segmentTitle={segmentTitle}
                segmentHref={segmentHref}
            />
        </Typography>
    );
};

export default TaskNameCell;
